import { useCallback, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useModals, ModalNames } from 'components/modals';
import useUI from 'hooks/use-ui';
import useUser from 'hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { InterfaceVariants } from 'src/constants/interface-variants';

const EXCLUDED_VARIANTS: InterfaceVariants[] = [
  InterfaceVariants.kiosk,
  InterfaceVariants.mobileEcomm,
  InterfaceVariants.plus,
];

export type TriggerPostSignupModalSequenceParams = {
  skipPostSignupSequence?: boolean;
};

export type UsePostSignupModalSequenceResult = {
  triggerPostSignupModalSequence: (params?: TriggerPostSignupModalSequenceParams) => Promise<void>;
};

export function usePostSignupModalSequence(): UsePostSignupModalSequenceResult {
  const { openModal } = useModals();
  const flags = useFlags();
  const { variant } = useUI();
  const User = useUser();
  const { dispensary } = useDispensary();
  const dispensaryId = dispensary?.id ?? null;
  const isRolloutEnabled = flags['fintech.platform.post-signup-onboarding-modals.rollout'] ?? false;

  const triggerPostSignupModalSequence = useCallback(
    async ({ skipPostSignupSequence = false }: TriggerPostSignupModalSequenceParams = {}): Promise<void> => {
      if (skipPostSignupSequence || !isRolloutEnabled || EXCLUDED_VARIANTS.includes(variant)) {
        return;
      }

      // Set synchronously, before any awaited calls below, so LoyaltyAccountDetection's
      // async-gated effect can never observe this as unset for this signup.
      User.postSignupModalSequenceDispensaryId = dispensaryId;

      await openModal(ModalNames.postSignupLoyaltyJoin);
      await openModal(ModalNames.postSignupBankLink);
      await openModal(ModalNames.postSignupLoyaltyPass);
    },
    [openModal, isRolloutEnabled, variant, User, dispensaryId]
  );

  return useMemo(() => ({ triggerPostSignupModalSequence }), [triggerPostSignupModalSequence]);
}
